import React from "react";
import { Badge, Box, Divider, Flex, SimpleGrid, Text, useColorModeValue } from "@chakra-ui/react";
import formatAmount from "./formatAmount";
import { CHANNEL_HINTS, CHANNEL_LABELS } from "./treasuryConstants";

/**
 * Renders one labelled figure of a currency position.
 * @param {Object} props - label, amount and currency to display, plus an optional tone
 * @returns {React.ReactElement}
 */
function Figure({ label, amount, currency, tone }) {
    const labelColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    const valueColor = useColorModeValue("secondaryGray.900", "white");
    return (
        <Box>
            <Text fontSize="xs" color={labelColor} mb="2px">{label}</Text>
            <Text fontSize="sm" fontWeight="700" color={tone || valueColor}>
                {formatAmount(amount, currency)}
            </Text>
        </Box>
    );
}

/**
 * Renders the coverage verdict of one currency: whether the rail can honour
 * what artists are still allowed to withdraw from it.
 * @param {Object} props - the normalized currency row
 * @returns {React.ReactElement}
 */
function CoverageLine({ row }) {
    const covered = row.coverage >= 0;
    const hintColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    return (
        <Box mt="12px">
            <Flex align="center" gap="8px" wrap="wrap">
                <Badge colorScheme={covered ? "green" : "red"} borderRadius="6px" px="8px">
                    {covered ? "Couvert" : "Découvert"}
                </Badge>
                <Text fontSize="sm" fontWeight="700" color={covered ? "green.400" : "red.400"}>
                    {formatAmount(row.coverage, row.currency)}
                </Text>
            </Flex>
            <Text fontSize="xs" color={hintColor} mt="6px">
                {covered
                    ? "Ce canal peut honorer les retraits de ses artistes."
                    : "Ce canal ne peut pas honorer les retraits de ses artistes : un transfert depuis l'autre canal est nécessaire."}
            </Text>
        </Box>
    );
}

/**
 * Renders the treasury position of a single currency inside its rail.
 * @param {Object} props - the normalized currency row
 * @returns {React.ReactElement}
 */
function CurrencyBlock({ row }) {
    const borderColor = useColorModeValue("secondaryGray.200", "whiteAlpha.200");
    const titleColor = useColorModeValue("secondaryGray.900", "white");
    return (
        <Box borderWidth="1px" borderColor={borderColor} borderRadius="12px" p="16px" mb="12px">
            <Text fontSize="md" fontWeight="700" color={titleColor} mb="12px">{row.currency}</Text>
            <SimpleGrid columns={{ base: 2, md: 4 }} gap="12px">
                <Figure label="Encaissé" amount={row.collected} currency={row.currency} />
                <Figure label="Déjà reversé" amount={row.paid_out} currency={row.currency} />
                <Figure label="Disponible" amount={row.available} currency={row.currency} />
                <Figure label="Dû aux artistes" amount={row.artist_obligations} currency={row.currency} />
            </SimpleGrid>
            {row.pending_payouts > 0 && (
                <Box mt="12px">
                    <Figure
                        label="Retraits demandés, non réglés"
                        amount={row.pending_payouts}
                        currency={row.currency}
                        tone="orange.400"
                    />
                </Box>
            )}
            <Divider my="12px" borderColor={borderColor} />
            <CoverageLine row={row} />
        </Box>
    );
}

/**
 * Renders one settlement rail with every currency it carries.
 * @param {Object} props - channel key and its normalized currency rows
 * @returns {React.ReactElement}
 */
function ChannelCard({ channel, rows }) {
    const cardBg = useColorModeValue("white", "navy.700");
    const titleColor = useColorModeValue("secondaryGray.900", "white");
    const hintColor = useColorModeValue("secondaryGray.600", "secondaryGray.400");
    const underCovered = rows.filter((row) => row.coverage < 0).length;

    return (
        <Box bg={cardBg} borderRadius="16px" p="20px" mb="20px">
            <Flex align="center" justify="space-between" gap="12px" mb="4px" wrap="wrap">
                <Text fontSize="lg" fontWeight="700" color={titleColor}>
                    {CHANNEL_LABELS[channel] || channel}
                </Text>
                {underCovered > 0 && (
                    <Badge colorScheme="red" borderRadius="6px" px="8px">
                        {underCovered} devise{underCovered > 1 ? "s" : ""} à découvert
                    </Badge>
                )}
            </Flex>
            <Text fontSize="sm" color={hintColor} mb="16px">{CHANNEL_HINTS[channel] || ""}</Text>
            {rows.map((row) => <CurrencyBlock key={row.currency} row={row} />)}
        </Box>
    );
}

export default ChannelCard;
